import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  Grid,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  TextField,
  Typography,
  autocompleteClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { IndexTable } from './IndexTable';
import { IndexTableProps } from './index_table.context';
import { TabIndexTableToolbar, TableFilter } from './TabIndexTableToolbar';
import { TitleTotalTabProps } from './tabs/TitleTotalTab';
import { ArrowDropDown } from '@mui/icons-material';

export interface TableAction {
  name: string;
  icon: JSX.Element;
  onClick: Function;
}

interface TabIndexTableProps extends IndexTableProps {
  filters?: TableFilter[];
  actions?: TableAction[];
  key: string;
}

export interface TabbedIndexTableProps {
  filterTitle?: string;
  tables: TabIndexTableProps[];
  initialTab?: number;
  accordian?: boolean;
  config?: {
    tabs: { [key: string]: TitleTotalTabProps };
    filters: TableFilter[][];
  };
  onConfigChange?: (config: any) => void;
  configLoading?: boolean;
}

export const TabIndexTable = ({
  tables,
  initialTab = 0,
  accordian = false,
  config,
  onConfigChange,
  configLoading = false,
  filterTitle,
}: TabbedIndexTableProps) => {
  const [value, setValue] = useState<number>(initialTab);
  const [open, setOpen] = useState<boolean>(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const downSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabs, setTabsActual] = useState<{ [key: string]: TitleTotalTabProps }>(
    config?.tabs || {}
  );
  const [filters, setFiltersActual] = useState<TableFilter[][]>([]);
  const setTabs = (tabs: { [key: string]: TitleTotalTabProps }) => {
    setTabsActual(tabs);
    onConfigChange && onConfigChange({ tabs: tabs });
  };
  const resetTabs = () => {
    onConfigChange && onConfigChange({ tabs: null });
  };
  const setFilters = (filters: TableFilter[][]) => {
    setFiltersActual(filters);
  };
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  useLayoutEffect(() => {
    let tabs: { [key: string]: TitleTotalTabProps } = {
      ...(config?.tabs || {}),
    };
    let filters: TableFilter[][] = [];
    tables?.forEach((table, index) => {
      tabs[table.key] = {
        title: table?.title,
        key: table?.key,
        sequence: tabs[table?.key]?.sequence || index,
        visible:
          tabs[table?.key]?.visible === false
            ? false
            : tabs[table?.key]?.visible || table?.visible,
        total: table?.rows?.length,
        value: index,
      } as TitleTotalTabProps;
      if (table.filters) {
        filters.push(table.filters);
      }
    });
    if (
      ![...Object.values(tabs)]?.sort((a, b) => a.sequence - b.sequence)?.[
        value
      ]?.visible
    ) {
      setValue(
        [...Object.values(tabs)]
          ?.sort((a, b) => a.sequence - b.sequence)
          ?.findIndex(t => t.visible)
      );
    }
    setTabsActual({ ...tabs });
    setFiltersActual(filters);
  }, [tables, config, value]);
  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'scroll',
        paddingBottom: downMd && Object.keys(tabs)?.length > 0 ? 12 : 0,
      }}
    >
      {filters && filters.length > 0 && (
        <>
          <Grid item md={12} lg={6} xl={5} sx={{ py: 2 }}>
            <Grid
              container
              direction="row"
              alignItems="end"
              spacing={2}
              flexWrap={'nowrap'}
            >
              <Grid item sx={{ pr: 1 }}>
                <Typography variant="h6">
                  {filters[0].length > 1
                    ? `${filterTitle || ''} Filters`
                    : `${filterTitle || ''} Filter`}
                  :
                </Typography>
              </Grid>
              {filters.map((filter, index) =>
                filter.map((f, i) => (
                  <Grid
                    item
                    sx={{
                      display: index === value ? 'block' : 'none',
                      flex: downMd ? 1 : 0,
                    }}
                    key={`${i}-${index}`}
                  >
                    <Autocomplete
                      id={`filter-${index}-${i}`}
                      disablePortal
                      disableClearable
                      options={f.options}
                      onChange={(event, value) => f.onChange(event, value)}
                      value={f.defaultOption}
                      getOptionLabel={(option: any) => {
                        if (typeof option === 'string') {
                          return option;
                        } else {
                          return (
                            option?.label || option?.title || option?.name || ''
                          );
                        }
                      }}
                      size="small"
                      renderInput={params => (
                        <TextField
                          {...params}
                          // label={f.label || ''}
                          InputProps={{
                            ...params.InputProps,
                          }}
                          variant="outlined"
                          sx={{
                            '& .MuiInput-underline:before': {
                              borderBottom: 'none',
                            },
                            '& .MuiInput-underline:after': {
                              borderBottom: 'none',
                            },
                          }}
                        />
                      )}
                      popupIcon={
                        f.loading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 24,
                              height: 24,
                            }}
                          >
                            <CircularProgress color="inherit" size={18} />
                          </Box>
                        ) : (
                          <ArrowDropDown />
                        )
                      }
                      fullWidth
                      sx={{
                        [`.${autocompleteClasses.inputRoot}`]: {
                          [`.${autocompleteClasses.input}`]: {
                            width: 'fit-content',
                          },
                        },
                        boxShadow: theme => theme.shadows[3],
                      }}
                    />
                    {/* <ControlledAutoComplete {...f} /> */}
                  </Grid>
                ))
              )}
            </Grid>
          </Grid>
          <Divider sx={{ mb: 1 }} />
        </>
      )}
      <Box>
        <TabIndexTableToolbar
          tabs={tabs}
          setTabs={setTabs}
          resetTabs={resetTabs}
          config={!!config}
          configLoading={configLoading}
          handleChange={handleChange}
          value={value}
          filters={filters}
          accordian={accordian}
        />
      </Box>
      {[...tables]
        ?.sort(
          (a, b) => tabs[a.title]?.sequence || 0 - tabs[b.title]?.sequence || 0
        )
        ?.filter(
          table =>
            [...Object.values(tabs)]?.sort((a, b) => a.sequence - b.sequence)?.[
              value
            ]?.key === table.key
        )
        ?.map((table, index) => (
          <Box
            sx={{ minHeight: '50vh', boxShadow: theme => theme.shadows[3] }}
            key={table.title}
          >
            <IndexTable {...table} toolbar={false} title={''} />
          </Box>
        ))}
      {tables[value]?.actions && (
        <SpeedDial
          ariaLabel="SpeedDial basic example"
          sx={{
            position: 'fixed',
            right: 0,
            px: 4,
            width: '100%',
            boxSizing: 'border-box',
            bottom:
              downMd && Object.keys(tabs)?.length > 0
                ? accordian
                  ? 130
                  : 88
                : accordian
                ? 106
                : 56,
            alignItems: downSm ? 'flex-end' : 'center',
          }}
          icon={<SpeedDialIcon id={`tab-index-table-speeddial`} />}
          onClose={handleClose}
          onOpen={handleOpen}
          open={open}
        >
          {tables[value]?.actions?.map((action, index) => (
            <SpeedDialAction
              key={index}
              icon={action.icon}
              tooltipTitle={action.name}
              tooltipOpen
              onClick={() => {
                action.onClick();
                handleClose();
              }}
            />
          ))}
        </SpeedDial>
      )}
    </Box>
  );
};
